import { computed, ref } from "vue";
import { defineStore } from "pinia";
import {
  EFeatureFlags,
  LocalStorageKeys,
  type IUserProfile
} from "@/core/@types/types";
import { fetchContratoPendenteFromAPI } from "@/core/services/financas/contratos/fetchContratoPendente";
import useDate from "@/core/plugins/useDate";
import useStorage from "@/core/plugins/useStorage";
import type { IAvisoContratoPendenteStorage, ILastTerm } from "./types";
import {
  canTermBeSignedByProfile,
  isSysAdminRole,
  isWebserviceEmail
} from "./permissions";

export const idContratoPendenteStore = "useContratoPendenteStore";
const financasTimeZone = "America/Sao_Paulo";
const intervaloExibicaoAvisoEmDias = 5;

function getDatePartsInFinancasTimeZone(date: Date) {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: financasTimeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit"
  }).formatToParts(date);

  return {
    year: parts.find(part => part.type === "year")?.value ?? "",
    month: parts.find(part => part.type === "month")?.value ?? "",
    day: parts.find(part => part.type === "day")?.value ?? ""
  };
}

function getIsoDateOnlyParts(date: string) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date.trim());

  if (!match) {
    return null;
  }

  const [, year, month, day] = match;

  return { year, month, day };
}

function getBrDateOnlyParts(date: string) {
  const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(date.trim());

  if (!match) {
    return null;
  }

  const [, day, month, year] = match;

  return { year, month, day };
}

function createLocalDateFromParts(parts: {
  year: string;
  month: string;
  day: string;
}) {
  return new Date(
    Number(parts.year),
    Number(parts.month) - 1,
    Number(parts.day)
  );
}

function getDateOnlyParts(date: string) {
  return getIsoDateOnlyParts(date) ?? getBrDateOnlyParts(date);
}

function isDateOnly(date: string) {
  return Boolean(getDateOnlyParts(date));
}

function getLocalDateOnly(date: string | Date) {
  if (date instanceof Date) {
    return createLocalDateFromParts(getDatePartsInFinancasTimeZone(date));
  }

  const parts = getDateOnlyParts(date);

  if (parts) {
    return createLocalDateFromParts(parts);
  }

  const parsedDate = new Date(date);

  if (Number.isNaN(parsedDate.getTime())) {
    return null;
  }

  return createLocalDateFromParts(getDatePartsInFinancasTimeZone(parsedDate));
}

function getLocalDateOnlyFromInstant(date: string) {
  const parts = getDateOnlyParts(date);

  if (parts) {
    return createLocalDateFromParts(parts);
  }

  const parsedDate = new Date(date);

  if (Number.isNaN(parsedDate.getTime())) {
    return null;
  }

  return createLocalDateFromParts(getDatePartsInFinancasTimeZone(parsedDate));
}

function addDays(date: Date, days: number) {
  const nextDate = new Date(date);
  nextDate.setDate(nextDate.getDate() + days);

  return nextDate;
}

function getDiffInDays(startDate: Date, endDate: Date) {
  return Math.floor(
    (endDate.getTime() - startDate.getTime()) / (24 * 60 * 60 * 1000)
  );
}

function formatLocalDate(date: Date) {
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();

  return `${day}/${month}/${year}`;
}

function formatStorageDate(date: Date) {
  const { year, month, day } = getDatePartsInFinancasTimeZone(date);

  return `${year}-${month}-${day}`;
}

function formatLocalDateOnlyForStorage(date: Date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
}

function getDataFinalAceite(term: ILastTerm) {
  if (term.dataPrazoAceite) {
    return getLocalDateOnlyFromInstant(term.dataPrazoAceite);
  }

  if (term.dataLimiteAceite) {
    return getLocalDateOnlyFromInstant(term.dataLimiteAceite);
  }

  if (!term.dataLimite) {
    return null;
  }

  const dataLimite = getLocalDateOnly(term.dataLimite);

  if (!dataLimite) {
    return null;
  }

  if (!isDateOnly(term.dataLimite)) {
    return dataLimite;
  }

  return addDays(dataLimite, -1);
}

export const useContratoPendenteStore = defineStore(
  idContratoPendenteStore,
  () => {
    const lastTerm = ref<ILastTerm | null>(null);
    const carregado = ref(false);
    const revisaoPermissoesUsuario = ref(0);
    const showModalBloqueio = ref(false);
    const isAssinaturaContratoEmAndamento = ref(false);
    const versaoControleAviso = ref(0);
    const storage = useStorage();

    function getUserProfile() {
      try {
        return storage.get<IUserProfile>(LocalStorageKeys.UserProfile) ?? null;
      } catch {
        return null;
      }
    }

    function getUserRoles() {
      try {
        return storage.get(LocalStorageKeys.userRules);
      } catch {
        return null;
      }
    }

    function hasGestaoContratosFeature() {
      return (
        getUserProfile()?.Features?.includes(EFeatureFlags.GestaoContratos) ??
        false
      );
    }

    function getUsuarioIdAviso() {
      const profile = getUserProfile();

      return String(
        profile?._id ?? profile?.CodigoCliente ?? profile?.Email ?? ""
      );
    }

    function getAvisoStorage(): IAvisoContratoPendenteStorage | null {
      let value: unknown;

      try {
        value = storage.get<unknown>(LocalStorageKeys.ContratoPendente);
      } catch {
        return null;
      }

      if (!value || typeof value !== "object") {
        return null;
      }

      const state = value as Partial<IAvisoContratoPendenteStorage>;

      if (
        typeof state.usuarioId !== "string" ||
        typeof state.termoId !== "string" ||
        !Array.isArray(state.visualizacoes) ||
        state.visualizacoes.some(item => typeof item !== "string") ||
        (state.adiadoAte !== undefined && typeof state.adiadoAte !== "string")
      ) {
        return null;
      }

      return state as IAvisoContratoPendenteStorage;
    }

    const isUsuarioSysAdmin = computed((): boolean => {
      lastTerm.value;

      return isSysAdminRole(getUserRoles());
    });

    const isUsuarioWebservice = computed((): boolean => {
      lastTerm.value;

      return isWebserviceEmail(getUserProfile()?.Email);
    });

    const podeConsultarPendencia = computed((): boolean => {
      return hasGestaoContratosFeature() && !isUsuarioSysAdmin.value;
    });

    const isPendente = computed((): boolean => {
      if (!hasGestaoContratosFeature()) {
        return false;
      }

      if (!lastTerm.value) {
        return false;
      }

      return !lastTerm.value.assinado;
    });

    const isTermoAutomatico = computed((): boolean => {
      const status = lastTerm.value?.status?.toLowerCase();
      const tipoAceite = lastTerm.value?.tipoAceite?.toLowerCase();

      return status === "automatico" || tipoAceite === "automatico";
    });

    const isBloqueioPorAtrasoAtivo = computed((): boolean => {
      return Boolean(
        lastTerm.value?.bloquearPorAtrasoGeral ||
        lastTerm.value?.bloquearPorAtrasoIndividual
      );
    });

    const podeAssinarTermoPendente = computed((): boolean => {
      // Os papéis do MVC chegam pelo localStorage e precisam invalidar este cálculo.
      revisaoPermissoesUsuario.value;

      return (
        hasGestaoContratosFeature() &&
        !isTermoAutomatico.value &&
        canTermBeSignedByProfile(
          lastTerm.value,
          getUserProfile(),
          getUserRoles()
        )
      );
    });

    const isBloqueioPorPrazo = computed((): boolean => {
      const dias = diasPrazoAssinaturaPendente.value;

      if (
        !isPendente.value ||
        dias === null ||
        !isBloqueioPorAtrasoAtivo.value
      ) {
        return false;
      }

      return dias < 0;
    });

    const diasPrazoAssinaturaPendente = computed((): number | null => {
      if (!lastTerm.value) {
        return null;
      }

      const libDate = useDate();
      const fimDoPrazo = getDataFinalAceite(lastTerm.value);

      if (!fimDoPrazo) {
        return null;
      }

      const hoje = getLocalDateOnly(libDate.now());

      if (!hoje) {
        return null;
      }

      return getDiffInDays(hoje, fimDoPrazo);
    });

    const isPrazoAceiteExpirado = computed((): boolean => {
      const dias = diasPrazoAssinaturaPendente.value;

      return dias !== null && dias < 0;
    });

    const podeExibirAvisoContratoPendente = computed((): boolean => {
      versaoControleAviso.value;

      if (!isPendente.value || !podeAssinarTermoPendente.value) {
        return false;
      }

      if (isBloqueioPorPrazo.value) {
        return true;
      }

      const usuarioId = getUsuarioIdAviso();
      const termoId = lastTerm.value?.id;
      const state = getAvisoStorage();
      const stateAtual =
        state?.usuarioId === usuarioId && state.termoId === termoId
          ? state
          : null;

      if (diasPrazoAssinaturaPendente.value === 0) {
        return true;
      }

      if (stateAtual?.adiadoAte) {
        const adiadoAte = getLocalDateOnly(stateAtual.adiadoAte);
        const hoje = getLocalDateOnly(useDate().now());

        if (adiadoAte && hoje && hoje < adiadoAte) {
          return false;
        }
      }

      if (isPrazoAceiteExpirado.value) {
        return true;
      }

      if (!stateAtual) {
        return true;
      }

      if (stateAtual.visualizacoes.length === 0) {
        return true;
      }

      const ultimaVisualizacao = getLocalDateOnly(
        stateAtual.visualizacoes[stateAtual.visualizacoes.length - 1]
      );
      const hoje = getLocalDateOnly(useDate().now());

      if (!ultimaVisualizacao || !hoje) {
        return true;
      }

      return (
        getDiffInDays(ultimaVisualizacao, hoje) >= intervaloExibicaoAvisoEmDias
      );
    });

    const dataLimiteAssinaturaPendenteFormatada = computed((): string => {
      if (!lastTerm.value) {
        return "";
      }

      const ultimoDiaAceite = getDataFinalAceite(lastTerm.value);

      if (!ultimoDiaAceite) {
        return "";
      }

      return formatLocalDate(ultimoDiaAceite);
    });

    const prazoLimiteAssinaturaPendente = computed((): string => {
      const dias = diasPrazoAssinaturaPendente.value;

      if (dias === null) {
        return "";
      }

      if (dias < 0) {
        return isBloqueioPorAtrasoAtivo.value
          ? "Prazo de aceite expirado: plataforma bloqueada temporariamente"
          : "Prazo de aceite expirado";
      }

      const dataLimite = dataLimiteAssinaturaPendenteFormatada.value;

      return dataLimite ? `Prazo para aceite: ${dataLimite}` : "";
    });

    function aplicarLastTerm(term: ILastTerm | null) {
      lastTerm.value = term;

      if (isBloqueioPorPrazo.value && podeAssinarTermoPendente.value) {
        showModalBloqueio.value = !isAssinaturaContratoEmAndamento.value;

        return;
      }

      isAssinaturaContratoEmAndamento.value = false;
      showModalBloqueio.value = false;
    }

    function registrarExibicaoAvisoContratoPendente() {
      if (
        !isPendente.value ||
        !podeAssinarTermoPendente.value ||
        isPrazoAceiteExpirado.value
      ) {
        return;
      }

      const usuarioId = getUsuarioIdAviso();
      const termoId = lastTerm.value?.id;

      if (!termoId) {
        return;
      }

      const current = getAvisoStorage();
      const visualizacoes =
        current?.usuarioId === usuarioId && current.termoId === termoId
          ? current.visualizacoes
          : [];

      const hoje = formatStorageDate(useDate().now());

      if (visualizacoes[visualizacoes.length - 1] === hoje) {
        return;
      }

      try {
        storage.delete(LocalStorageKeys.ContratoPendente);
        storage.set<IAvisoContratoPendenteStorage>(
          LocalStorageKeys.ContratoPendente,
          {
            usuarioId,
            termoId,
            visualizacoes: [...visualizacoes, hoje]
          }
        );
        versaoControleAviso.value++;
      } catch {
        // A indisponibilidade do storage nao deve impedir a exibicao do aviso.
      }
    }

    function adiarAvisoContratoPendenteParaProximoDia() {
      if (!isPendente.value || !podeAssinarTermoPendente.value) {
        return;
      }

      const usuarioId = getUsuarioIdAviso();
      const termoId = lastTerm.value?.id;

      if (!termoId) {
        return;
      }

      const current = getAvisoStorage();
      const visualizacoes =
        current?.usuarioId === usuarioId && current.termoId === termoId
          ? current.visualizacoes
          : [];
      const hoje = getLocalDateOnly(useDate().now());

      if (!hoje) {
        return;
      }

      try {
        storage.delete(LocalStorageKeys.ContratoPendente);
        storage.set<IAvisoContratoPendenteStorage>(
          LocalStorageKeys.ContratoPendente,
          {
            usuarioId,
            termoId,
            visualizacoes,
            adiadoAte: formatLocalDateOnlyForStorage(addDays(hoje, 1))
          }
        );
        versaoControleAviso.value++;
      } catch {
        // Sem storage, o aviso segue elegivel quando a campanha for fechada.
      }
    }

    function closeModalBloqueio() {
      showModalBloqueio.value = false;
    }

    function iniciarAssinaturaContrato() {
      if (!podeAssinarTermoPendente.value) {
        return;
      }

      isAssinaturaContratoEmAndamento.value = true;
    }

    function finalizarAssinaturaContrato() {
      isAssinaturaContratoEmAndamento.value = false;

      if (isBloqueioPorPrazo.value && podeAssinarTermoPendente.value) {
        showModalBloqueio.value = true;
      }
    }

    async function fetchContratoPendente() {
      if (!podeConsultarPendencia.value) {
        aplicarLastTerm(null);
        carregado.value = true;

        return;
      }

      const result = await fetchContratoPendenteFromAPI();

      carregado.value = true;

      if (result.isSuccess) {
        aplicarLastTerm(result.getValue());
      }
    }

    function reavaliarPermissoesUsuario() {
      revisaoPermissoesUsuario.value += 1;
    }

    return {
      lastTerm,
      carregado,
      showModalBloqueio,
      isAssinaturaContratoEmAndamento,

      isUsuarioSysAdmin,
      isUsuarioWebservice,
      podeConsultarPendencia,
      podeAssinarTermoPendente,
      isTermoAutomatico,
      isBloqueioPorAtrasoAtivo,
      isPendente,
      isBloqueioPorPrazo,
      isPrazoAceiteExpirado,
      podeExibirAvisoContratoPendente,
      prazoLimiteAssinaturaPendente,
      dataLimiteAssinaturaPendenteFormatada,

      aplicarLastTerm,
      reavaliarPermissoesUsuario,
      registrarExibicaoAvisoContratoPendente,
      adiarAvisoContratoPendenteParaProximoDia,
      closeModalBloqueio,
      iniciarAssinaturaContrato,
      finalizarAssinaturaContrato,
      fetchContratoPendente
    };
  }
);
